import { useEffect, useState, useCallback, ReactElement } from "react";
import { useRecoilValue } from "recoil";

import { IClip, IUserProfile, IUnclaimedProfile } from "MVVM/@types";
import { usePortal } from "MVVM/Hooks/usePortal";
import { MatchHistoryGame } from "MVVM/State/matchHistory";
import { MatchHistoryLoginModal } from "MVVM/Views/MatchHistory/Components/Shared/LoginModal";
import { getMatchHistoryGameEnum } from "MVVM/Views/MatchHistory/utils";
import { LoginUpsell } from "MVVM/Components/LoginUpsell";
import { ClaimProfileUpsell } from "MVVM/Components/ClaimProfileUpsell";
import { isUnclaimedProfile } from "MVVM/Utilities/User";

const isModalCookieSet = () =>
  document.cookie
    .split("; ")
    .some((cookie) => cookie.startsWith("login_modal_shown="));

export const useLoginModal = (
  allstarUser: any,
  delay = 5000,
  clip?: IClip,
  user?: IUserProfile,
) => {
  const matchHistoryGame = useRecoilValue(MatchHistoryGame);
  const isClaimableProfile = user && isUnclaimedProfile(user);

  const [modalContent, setModalContent] = useState<ReactElement | null>(null);
  const [watchedClip, setWatchedClip] = useState<IClip | undefined>(undefined);

  if (!modalContent && clip?.pointOfView === "OTHER") {
    setModalContent(
      <MatchHistoryLoginModal
        game={getMatchHistoryGameEnum(clip.game) || matchHistoryGame}
        clip={clip}
        title="signUpToClaim.title"
        defaultTitle="See Yourself In This Clip?"
        subtitle="signUpToClaim.subtitle"
        defaultSubtitle="Claim this clip and we`ll add it to your profile, so you can personalize it with your own filters &amp; effects, and share with friends!"
      />,
    );
  }

  if (!watchedClip && clip) {
    setWatchedClip(clip);
  }

  const { portalOpen, setPortalOpen, Portal } = usePortal(
    isClaimableProfile ? (
      <ClaimProfileUpsell
        user={user as IUnclaimedProfile}
        onClose={() => setPortalOpen(false)}
      />
    ) : (
      modalContent || <LoginUpsell clip={watchedClip} />
    ),
  );

  const popModal = useCallback(() => {
    if (
      (Object.keys(allstarUser).length === 0 || !allstarUser.loggedIn) &&
      !isModalCookieSet()
    ) {
      document.cookie = `login_modal_shown=true; max-age=${
        12 * 60 * 60
      }; path=/`;
      setPortalOpen(true);

      return true;
    }

    return false;
  }, [allstarUser, setPortalOpen]);

  useEffect(() => {
    if (!delay) return;
    const timeout = setTimeout(popModal, delay);

    return () => clearTimeout(timeout);
  }, [allstarUser, delay, setPortalOpen, popModal]);

  return { portalOpen, popModal, setPortalOpen, Portal };
};
